import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GungBase, Customer } from 'gung-standard';
import { Observable, BehaviorSubject, tap, of, catchError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ColmecPartnerAgreementsService {
  private partnerAgreementSubject: BehaviorSubject<ColmecPartnerAgreement[]>;
  private baseUrl: string = 'json/partner-agreements';

  constructor(protected http: HttpClient) {}

  public getPartnerAgreementsByPartner(id: string, nocache?: boolean): Observable<ColmecPartnerAgreement[]> {
    const url = `${this.baseUrl}/by-partner-id/${id}`;
    const headers = {
      maxAge: nocache ? '-1' : '300'
    };
    return this.http.get<ColmecPartnerAgreement[]>(url, { headers }).pipe(
      catchError(_ => {
        return of([]);
      })
    );
  }

  public getPartnerAgreementById(id: string, nocache?: boolean): Observable<ColmecPartnerAgreement> {
    const url = `${this.baseUrl}/${id}`;
    const headers = {
      maxAge: nocache ? '-1' : '300'
    };
    return this.http.get<ColmecPartnerAgreement>(url, { headers }).pipe(
      catchError(_ => {
        return of(undefined);
      })
    );
  }

  public getPartnerAgreements(nocache?: boolean): Observable<ColmecPartnerAgreement[]> {
    if (!this.partnerAgreementSubject) {
      this.partnerAgreementSubject = new BehaviorSubject<ColmecPartnerAgreement[]>([]);
      this.updatePartnerAgreementSubject(nocache);
    }
    return this.partnerAgreementSubject.asObservable();
  }

  private updatePartnerAgreementSubject(nocache?: boolean): void {
    if (!this.partnerAgreementSubject) {
      this.partnerAgreementSubject = new BehaviorSubject<ColmecPartnerAgreement[]>([]);
    }
    this.getPartnerAgreementsInternal(nocache).subscribe((data: ColmecPartnerAgreement[]) =>
      this.partnerAgreementSubject.next(data)
    );
  }

  public getPartnerAgreementsInternal(nocache?: boolean): Observable<ColmecPartnerAgreement[]> {
    const headers = {
      maxAge: nocache ? '-1' : '300'
    };
    return this.http.get<ColmecPartnerAgreement[]>(this.baseUrl, { headers }).pipe(
      catchError(_ => {
        return of([]);
      })
    );
  }

  public createPartnerAgreement(data: ColmecPartnerAgreement): Observable<any> {
    return this.http
      .post<ColmecPartnerAgreement[]>(this.baseUrl, data)
      .pipe(tap(_ => this.updatePartnerAgreementSubject(true)));
  }

  public updatePartnerAgreement(data: ColmecPartnerAgreement): Observable<any> {
    const url = `${this.baseUrl}/${data.id}`;
    return this.http.put<ColmecPartnerAgreement[]>(url, data).pipe(tap(_ => this.updatePartnerAgreementSubject(true)));
  }

  public archivePartnerAgreement(data: ColmecPartnerAgreement): Observable<any> {
    const url = `${this.baseUrl}/archive`;
    return this.http.put<any>(url, data).pipe(tap(_ => this.updatePartnerAgreementSubject(true)));
  }

  public restorePartnerAgreement(data: ColmecPartnerAgreement): Observable<any> {
    const url = `${this.baseUrl}/restore`;
    return this.http.put<any>(url, data).pipe(tap(_ => this.updatePartnerAgreementSubject(true)));
  }

  public deletePartnerAgreement(data: ColmecPartnerAgreement): Observable<any> {
    const url = `${this.baseUrl}/${data.id}`;
    return this.http.delete<any>(url).pipe(tap(_ => this.updatePartnerAgreementSubject(true)));
  }
}

export interface ColmecPartnerAgreement extends GungBase {
  agreementFileS3Uri: string;
  customerId: string;
  customerName: string;
  partnerId: string;
  partnerName: string;
  servicePriceListId: string;
  supplierId: string;
  status: ColmecPartnerAgreementStatus;
}

export enum ColmecPartnerAgreementStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED'
}
